import type { ReactNode } from 'react'
import { Badge, Group, Paper, Stack, Text } from '@mantine/core'
import { IconCake, IconMail, IconPhone } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import type { UpcomingBirthdayDto } from '@ledokol/contracts'

const ROLE_LABEL: Record<string, string> = {
  decision_maker: 'ЛПР',
  cmo: 'CMO',
  marketer: 'Маркетолог',
  procurement: 'Закупки',
  finance: 'Финансы',
  legal: 'Юрист',
  other: 'Другое',
}

function daysUntilLabel(d: number): string {
  if (d === 0) return 'СЕГОДНЯ'
  if (d === 1) return 'завтра'
  if (d < 5) return `через ${d} дня`
  if (d < 21) return `через ${d} дней`
  if (d % 10 === 1 && d % 100 !== 11) return `через ${d} день`
  if (d % 10 >= 2 && d % 10 <= 4 && (d % 100 < 12 || d % 100 > 14)) return `через ${d} дня`
  return `через ${d} дней`
}

export function BirthdayCard({
  item,
  showProject = true,
  action,
}: {
  item: UpcomingBirthdayDto
  showProject?: boolean
  /** #6: слот под кнопку «Поздравить» (рендерит страница под правом greetings.manage). */
  action?: ReactNode
}) {
  const urgent = item.days_until === 0
  const soon = item.days_until <= 7
  const isEmployee = item.subject_type === 'employee'

  return (
    <Paper p="md" withBorder radius="md" style={urgent ? { borderColor: '#fa5252', borderWidth: 2 } : undefined}>
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
          <Group gap="xs">
            <IconCake size={18} color={urgent ? '#fa5252' : soon ? '#fab005' : '#868e96'} />
            <Text fw={600}>{item.full_name}</Text>
            {isEmployee ? (
              <Badge size="sm" color="blue" variant="light">Сотрудник</Badge>
            ) : (
              <Badge size="sm" color="grape" variant="light">
                {(item.contact_role && ROLE_LABEL[item.contact_role]) ?? item.contact_role ?? 'Контакт'}
              </Badge>
            )}
            {item.position && <Text size="sm" c="dimmed">— {item.position}</Text>}
          </Group>

          {showProject && item.project && (
            <Text component={Link} to={`/projects/${item.project.id}/details`} size="sm" c="blue" td="underline">
              {item.project.name}
            </Text>
          )}

          <Group gap="md" mt={4}>
            {item.phone && (
              <Text component="a" href={`tel:${item.phone}`} size="sm" c="blue">
                <IconPhone size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {item.phone}
              </Text>
            )}
            {item.email && (
              <Text component="a" href={`mailto:${item.email}`} size="sm" c="blue">
                <IconMail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {item.email}
              </Text>
            )}
          </Group>
        </Stack>

        <Stack gap={4} align="flex-end">
          <Text fw={700} c={urgent ? 'red' : soon ? 'orange' : undefined} size="lg">
            {daysUntilLabel(item.days_until)}
          </Text>
          <Text size="sm" c="dimmed">{dayjs(item.next_birthday).format('D MMMM')}</Text>
          {action}
        </Stack>
      </Group>
    </Paper>
  )
}
